import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button as ButtonV2, Heading, Modal, ModalContent, showToast } from '@restworld/ui-ds-base';
import { IRestaurantsPayload, useRestaurantsService } from '@restworld/data-services';

import { restaurantDetailsAtom, restaurantInformationQueryKey } from '.';

export const toggleVenuePublicConfirmAtom = atom<boolean>(false);

function useUpdateRestaurantMutation() {
  const restaurantsService = useRestaurantsService();
  return useMutation({
    mutationFn: async ({
      restaurantId,
      restaurantPayload,
    }: {
      restaurantId: string;
      restaurantPayload: IRestaurantsPayload;
    }) => (await restaurantsService.updateRestaurant({ restaurantId, params: restaurantPayload })).data,
  });
}

export function useChangeRestaurantVisibility() {
  const { restaurantId, showRestaurantDetails } = useAtomValue(restaurantDetailsAtom) || {};
  const { mutate: updateRestaurantShowcaseVisibility, isPending, isError, isSuccess } = useUpdateRestaurantMutation();
  const setIsShowcaseVisibilityToggleModal = useSetAtom(toggleVenuePublicConfirmAtom);
  const { t } = useTranslation('restaurant-showcase-v2');

  const handleToggleVisibility = useCallback(
    () =>
      updateRestaurantShowcaseVisibility({
        restaurantId,
        restaurantPayload: { show_details: !showRestaurantDetails } as IRestaurantsPayload,
      }),
    [restaurantId, showRestaurantDetails, updateRestaurantShowcaseVisibility]
  );
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (isSuccess) {
      queryClient.invalidateQueries({ queryKey: restaurantInformationQueryKey });
      showToast(t('publish_showcase_success'), 'success', {
        autoClose: 3000,
        position: 'top-right',
      });
      setIsShowcaseVisibilityToggleModal(false);
    }
  }, [isSuccess, queryClient, setIsShowcaseVisibilityToggleModal, t]);

  React.useEffect(() => {
    if (isError) {
      showToast(showRestaurantDetails ? t('unpublish_showcase_error') : t('publish_showcase_error'), 'error', {
        autoClose: 3000,
        position: 'top-right',
      });
    }
  }, [isError, setIsShowcaseVisibilityToggleModal, showRestaurantDetails, t]);

  return { handleToggleVisibility, isPending };
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IVenuePublicConfirmModalProps {}
const VenuePublicConfirmModal: React.FunctionComponent<IVenuePublicConfirmModalProps> = (props) => {
  const { t } = useTranslation('restaurant-showcase-v2');
  const [isShowcaseVisibilityToggleModalOpen, setIsShowcaseVisibilityToggleModalOpen] =
    useAtom(toggleVenuePublicConfirmAtom);
  const { handleToggleVisibility, isPending } = useChangeRestaurantVisibility();
  const isRestaurantPublic = useAtomValue(restaurantDetailsAtom)?.showRestaurantDetails;

  const closeModal = useCallback(
    () => setIsShowcaseVisibilityToggleModalOpen(false),
    [setIsShowcaseVisibilityToggleModalOpen]
  );

  return (
    <Modal open={isShowcaseVisibilityToggleModalOpen} onOpenChange={(v) => !v && closeModal()}>
      <ModalContent
        footer={
          <div className="flex flex-col gap-[10px] w-full">
            <ButtonV2
              variant={'secondary-success'}
              StartIcon={GlobeAltIcon}
              fullwidth
              onClick={handleToggleVisibility}
              loading={isPending}
            >
              {isRestaurantPublic ? t('hide_showcase') : t('publish_showcase')}
            </ButtonV2>
            <ButtonV2 variant="tertiary" fullwidth onClick={closeModal}>
              {t('cancel')}
            </ButtonV2>
          </div>
        }
      >
        <div className="text-center flex flex-col items-center">
          <Heading color="neutral">
            {t(
              isRestaurantPublic
                ? 'you_are_about_to_hide_your_showcase'
                : 'you_are_about_to_publish_your_venue_showcase'
            )}
          </Heading>
          <Heading size={5} weight="normal" color="neutral" className="mt-[10px] mb-12 max-w-[560px]">
            {t(
              isRestaurantPublic
                ? 'people_will_no_longer_be_able_to_access_your_showcase_key'
                : 'people_will_be_able_to_view_the_showcase_key'
            )}
          </Heading>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default VenuePublicConfirmModal;
